import settings from '../config/settings';

/**
 * User Cache Utility
 * Handles caching of user data in session storage
 */

const {
  userCacheKey: CACHE_KEY,
  userCacheExpiry: CACHE_EXPIRY_MS
} = settings.cache;

// localStorage (not sessionStorage) so the daily TTL survives browser/tab restarts
// and refreshes on a real 24h clock instead of "whenever the session resets".
const store = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : {
  getItem: () => null, setItem: () => {}, removeItem: () => {}
};

/**
 * Get users from cache if they exist and aren't expired
 * @returns {Object|null} Cached users and timestamp or null if invalid/expired
 */
export const getUsersFromCache = () => {
  try {
    const cachedData = store.getItem(CACHE_KEY);
    if (!cachedData) {
      console.log('No cached users found');
      return null;
    }

    const { users, timestamp } = JSON.parse(cachedData);
    const cacheAge = Date.now() - timestamp;
    const isExpired = cacheAge > CACHE_EXPIRY_MS;

    console.log(`User cache info - Age: ${Math.round(cacheAge/1000/60)}m, ` +
                `Expires in: ${Math.max(0, Math.round((CACHE_EXPIRY_MS - cacheAge)/1000/60))}m`);

    if (isExpired) {
      console.log('User cache expired, needs refresh');
      return { users, timestamp, isExpired: true };
    }

    return { users, timestamp, isExpired: false };
  } catch (error) {
    console.error('Error reading users from cache:', error);
    return null;
  }
};

/**
 * Save users to cache with timestamp
 * @param {Array} users - Array of user objects
 * @returns {boolean} - True if successful, false otherwise
 */
export const saveUsersToCache = (users) => {
  try {
    const cacheData = {
      users,
      timestamp: Date.now()
    };
    store.setItem(CACHE_KEY, JSON.stringify(cacheData));
    console.log(`Users cached successfully at ${new Date().toISOString()}`);
    return true;
  } catch (error) {
    console.error('Error saving users to cache:', error);
    return false;
  }
};

/**
 * Clear the user cache
 * @returns {boolean} - True if successful, false otherwise
 */
export const clearUserCache = () => {
  try {
    store.removeItem(CACHE_KEY);
    console.log('User cache cleared');
    return true;
  } catch (error) {
    console.error('Error clearing user cache:', error);
    return false;
  }
};

/**
 * Check if user cache needs refresh (expired or doesn't exist)
 * @returns {boolean}
 */
export const needsUserCacheRefresh = () => {
  const cache = getUsersFromCache();
  return !cache || cache.isExpired;
};

/**
 * Get the time remaining until user cache expires (in milliseconds)
 * @returns {number} Time in milliseconds, or 0 if no cache or already expired
 */
export const getTimeUntilUserCacheExpiry = () => {
  const cache = getUsersFromCache();
  if (!cache) return 0;
  
  const timeElapsed = Date.now() - cache.timestamp;
  return Math.max(0, CACHE_EXPIRY_MS - timeElapsed);
};

/**
 * Initialize the user cache if needed
 * @returns {Object} Cache status and data
 */
export const initializeUserCache = async () => {
  console.log('Initializing user cache...');
  
  try {
    const cache = getUsersFromCache();
    console.log('Current user cache state:', {
      exists: !!cache,
      isExpired: cache?.isExpired,
      userCount: cache?.users?.length || 0,
      timestamp: cache?.timestamp ? new Date(cache.timestamp).toISOString() : 'N/A'
    });
    
    if (!cache || cache.isExpired) {
      console.log('User cache needs refresh');
      
      // Return a function to refresh the cache
      return {
        needsRefresh: true,
        data: cache?.users || [],
        fromCache: false,
        refresh: async () => {
          console.log('Starting user cache refresh...');
          
          try {
            // Use backend API - no need for WooCommerce credentials
            const apiBaseUrl = settings.api.baseUrl || (typeof window !== 'undefined' ? `http://${window.location.hostname}:5000` : 'http://localhost:5000');
            
            console.log('Using backend API:', {
              baseUrl: apiBaseUrl,
              endpoint: '/api/kiosk-users'
            });
            
            const url = new URL(`${apiBaseUrl}/api/kiosk-users`);
            // Note: No need to pass consumer_key/secret - backend handles WooCommerce auth
            
            console.log('Fetching users from:', url.toString());
            
            const startTime = Date.now();
            const response = await fetch(url.toString(), {
              headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              },
              // Add timeout for the request
              signal: AbortSignal.timeout(30000) // 30 seconds timeout
            });
            
            const responseTime = Date.now() - startTime;
            console.log(`API response received in ${responseTime}ms with status:`, response.status);
            
            if (!response.ok) {
              let errorDetails = '';
              try {
                const errorBody = await response.text();
                errorDetails = `Response: ${errorBody}`;
                console.error('API Error Response:', errorBody);
              } catch (e) {
                errorDetails = 'Could not parse error response';
              }
              
              throw new Error(
                `Failed to fetch users: ${response.status} ${response.statusText}. ${errorDetails}`
              );
            }
            
            const data = await response.json();
            // Backend /api/kiosk-users returns { users: [...], pagination: { synced_at, ... } } (from DB)
            const users = Array.isArray(data?.users) ? data.users : (Array.isArray(data) ? data : []);
            console.log(`Successfully fetched ${users.length} users from API (DB)`);
            
            if (users.length === 0 && data?.pagination) {
              console.log('Response has pagination; only first page cached. Synced at:', data.pagination.synced_at);
            }
            
            const saveResult = saveUsersToCache(users);
            if (!saveResult) {
              console.warn('Failed to save users to cache');
            }
            
            return {
              success: true,
              users,
              fromCache: false,
              timestamp: Date.now()
            };
            
          } catch (error) {
            console.error('Error in user cache refresh:', {
              error: error.message,
              name: error.name,
              stack: error.stack
            });
            
            return {
              success: false,
              error: error.message,
              users: cache?.users || [],
              fromCache: false,
              cacheAvailable: !!cache,
              timestamp: cache?.timestamp
            };
          }
        }
      };
    }
    
    // Return cached data if it exists and is not expired
    return {
      needsRefresh: false,
      data: cache.users,
      fromCache: true,
      timestamp: cache.timestamp
    };
    
  } catch (error) {
    console.error('Error in initializeUserCache:', {
      error: error.message,
      name: error.name,
      stack: error.stack
    });
    
    // Return a safe default in case of initialization error
    return {
      needsRefresh: true,
      data: [],
      fromCache: false,
      error: error.message,
      timestamp: null
    };
  }
  
  return {
    needsRefresh: false,
    data: cache.users,
    fromCache: true
  };
};

/**
 * Check if user cache is valid (exists and not expired)
 * @returns {boolean}
 */
export const isUserCacheValid = () => {
  const cache = getUsersFromCache();
  return cache && !cache.isExpired;
};
